import { Card } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
import { BaseLink } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { formatDistanceInMiles } from "@src/appV2/lib/utils/distance";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { WorkplaceNameWithDistance } from "@src/appV2/redesign/Shift/Open/WorkplaceNameWithDistance";
import { InterviewStatus } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { utcToZonedTime } from "date-fns-tz";

import { type PlacementWithDetails } from "../../api/useFetchPaginatedPlacements";
import { getPlacementDetailsPath } from "../../paths";
import { getDocumentUploadDeadline } from "../../utils/reliabilityRule";
import { BookedInterviewCardTitle } from "../AppliedPlacementCard/components/BookedInterviewCardTitle";
import { InterviewStatusPill } from "../AppliedPlacementCard/components/InterviewStatusPill";
import { PlacementWorkerTypePill } from "../PlacementCard/components/PlacementWorkerTypePill";

interface PendingInterviewCardProps {
  placement: PlacementWithDetails;
  placementCandidate: PlacementCandidate;
  workerTimezone: string;
  index: number;
}

export function PendingInterviewCard(props: PendingInterviewCardProps) {
  const { placement, placementCandidate, workerTimezone, index } = props;

  // Find the pending interview from the placement's interviews
  const pendingInterview = placement.interviews.find(
    (interview) => interview.attributes.status === InterviewStatus.PENDING
  );

  // Don't render if there's no pending interview
  if (!isDefined(pendingInterview)) {
    return null;
  }

  const interviewStart = utcToZonedTime(
    parseISO(pendingInterview.attributes.start),
    workerTimezone
  );
  const interviewEnd = utcToZonedTime(parseISO(pendingInterview.attributes.end), workerTimezone);

  // Document deadline is 4 PM the day before the interview in worker's timezone
  const documentDeadline = getDocumentUploadDeadline({
    interviewStart,
    workerTimezone,
  });

  const detailsPath = getPlacementDetailsPath(placement.id, placementCandidate.id, {
    cardType: undefined,
    autoOpenInterviewBooking: false,
  });

  return (
    <Card
      variant="primary"
      sx={{
        border: (theme) => `1px solid ${theme.palette.border?.subtle}`,
        background: (theme) => theme.palette.background.quaternary,
      }}
    >
      <CardActionArea
        component={BaseLink}
        to={detailsPath}
        onClick={() => {
          logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
            placementCandidateId: placementCandidate.id,
            placementId: placement.id,
            listIndex: index + 1,
            source: "pending_interviews",
          });
        }}
      >
        <CardContent>
          <Stack spacing={5} sx={{ paddingTop: 2 }}>
            <Stack spacing={3}>
              <BookedInterviewCardTitle
                interviewStart={interviewStart}
                interviewEnd={interviewEnd}
                workerTimezone={workerTimezone}
              />
              <WorkplaceNameWithDistance
                textVariant="body1"
                workplaceName={placement.workplace.name}
                formattedDistance={formatDistanceInMiles(placement.distance ?? 0)}
              />
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                {isDefined(placement.workerTypes) && (
                  <PlacementWorkerTypePill workerTypes={placement.workerTypes} />
                )}
              </Box>
              <Box>
                <InterviewStatusPill
                  status={pendingInterview.attributes.status}
                  documentDeadline={documentDeadline}
                  timezone={workerTimezone}
                  isBanExcused={false}
                />
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
